import { useEffect, useMemo, useRef, useState } from 'react'
import { Expand, Pause, Play, Settings, Volume2, VolumeX } from 'lucide-react'
import { cn } from '@/shared/lib'

type CatalogVideoPlayerProps = {
  src: string
  poster?: string | null
  title: string
  className?: string
  initialProgressSeconds?: number | null
  // Called when playback gets stuck or a media error occurs so parent can refresh signed URLs.
  onPlaybackError?: () => void
  onPlaybackCheckpoint?: (payload: {
    positionSeconds: number
    durationSeconds: number | null
    completed: boolean
  }) => void
}

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2]
const EMPTY_CAPTIONS_VTT = 'data:text/vtt;charset=utf-8,WEBVTT%0A%0A'

const formatDuration = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00'
  }

  const rounded = Math.floor(seconds)
  const hours = Math.floor(rounded / 3600)
  const minutes = Math.floor((rounded % 3600) / 60)
  const secs = rounded % 60

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  return `${minutes}:${String(secs).padStart(2, '0')}`
}

const formatRate = (rate: number) => `${rate}x`

export function CatalogVideoPlayer({
  src,
  poster,
  title,
  className,
  initialProgressSeconds = null,
  onPlaybackError,
  onPlaybackCheckpoint,
}: CatalogVideoPlayerProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const previousErrorRef = useRef<unknown>(null)
  const playRecoveryTimeoutRef = useRef<number | null>(null)
  const hasAppliedInitialSeekRef = useRef(false)
  const lastCheckpointSecondRef = useRef<number>(-1)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [seekDraft, setSeekDraft] = useState<number | null>(null)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [muted, setMuted] = useState(false)
  const [volume, setVolume] = useState(1)
  const [paused, setPaused] = useState(true)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [isWaiting, setIsWaiting] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [hasPlaybackError, setHasPlaybackError] = useState(false)

  useEffect(() => {
    previousErrorRef.current = null
    hasAppliedInitialSeekRef.current = false
    lastCheckpointSecondRef.current = -1
  }, [src])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement))
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  useEffect(() => {
    const video = videoRef.current

    return () => {
      if (playRecoveryTimeoutRef.current !== null) {
        window.clearTimeout(playRecoveryTimeoutRef.current)
      }

      if (!video) {
        return
      }

      // Stop playback immediately when the route/component changes.
      video.pause()
      video.removeAttribute('src')
      video.load()
    }
  }, [])

  useEffect(() => {
    if (!isSettingsOpen) {
      return
    }

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target
      if (!rootRef.current || !(target instanceof Node) || rootRef.current.contains(target)) {
        return
      }

      setIsSettingsOpen(false)
    }

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsSettingsOpen(false)
      }
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onEscape)

    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onEscape)
    }
  }, [isSettingsOpen])

  const effectiveSeekTime = seekDraft ?? currentTime
  const seekPercent = duration > 0 ? Math.min((effectiveSeekTime / duration) * 100, 100) : 0
  const volumeValue = muted ? 0 : volume

  const clearPlayRecoveryTimeout = () => {
    if (playRecoveryTimeoutRef.current === null) {
      return
    }

    window.clearTimeout(playRecoveryTimeoutRef.current)
    playRecoveryTimeoutRef.current = null
  }

  const emitPlaybackCheckpoint = (
    positionSecondsValue: number,
    durationSecondsValue: number | null,
    completedValue: boolean
  ) => {
    const normalizedPosition = Math.max(0, Math.floor(positionSecondsValue))
    const normalizedDuration =
      typeof durationSecondsValue === 'number' && Number.isFinite(durationSecondsValue)
        ? Math.max(0, Math.floor(durationSecondsValue))
        : null

    onPlaybackCheckpoint?.({
      positionSeconds: normalizedPosition,
      durationSeconds: normalizedDuration,
      completed: completedValue,
    })
  }

  const schedulePlayRecovery = (video: HTMLVideoElement) => {
    clearPlayRecoveryTimeout()
    playRecoveryTimeoutRef.current = window.setTimeout(() => {
      const seemsStuck =
        !video.error &&
        !video.paused &&
        video.currentTime < 0.05 &&
        video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA

      if (!seemsStuck) {
        return
      }

      setPaused(true)
      setIsWaiting(true)
      onPlaybackError?.()

      video.load()
      void video.play().catch(() => {
        setPaused(true)
      })
    }, 3500)
  }

  useEffect(() => {
    const video = videoRef.current
    if (!video) {
      return
    }

    clearPlayRecoveryTimeout()
    video.pause()
    video.src = src
    video.load()
  }, [src])

  const commitSeek = (value: number) => {
    const video = videoRef.current
    if (!video || duration <= 0 || !Number.isFinite(value)) {
      return
    }

    const boundedTime = Math.min(Math.max(value, 0), duration)
    video.currentTime = boundedTime
    setCurrentTime(boundedTime)
    setSeekDraft(null)
  }

  const togglePaused = () => {
    const video = videoRef.current
    if (!video) {
      return
    }

    if (video.paused) {
      void video.play().catch(() => {
        setPaused(true)
      })
      schedulePlayRecovery(video)
      return
    }

    clearPlayRecoveryTimeout()
    video.pause()
  }

  const changeVolume = (value: number) => {
    const video = videoRef.current
    if (!video) {
      return
    }

    const bounded = Math.min(Math.max(value, 0), 1)
    video.volume = bounded
    video.muted = bounded <= 0
    setVolume(bounded)
    setMuted(video.muted)
  }

  const toggleMute = () => {
    const video = videoRef.current
    if (!video) {
      return
    }

    video.muted = !video.muted
    setMuted(video.muted)
  }

  const toggleFullscreen = async () => {
    const root = rootRef.current
    if (!root) {
      return
    }

    if (document.fullscreenElement) {
      await document.exitFullscreen().catch(() => undefined)
      return
    }

    await root.requestFullscreen().catch(() => undefined)
  }

  const applyPlaybackRate = (rate: number) => {
    const video = videoRef.current
    if (!video) {
      return
    }

    video.playbackRate = rate
    setPlaybackRate(rate)
  }

  const controlsTime = useMemo(
    () => `${formatDuration(currentTime)} / ${formatDuration(duration)}`,
    [currentTime, duration]
  )

  return (
    <div ref={rootRef} className={cn('absolute inset-0', className)}>
      <video
        ref={videoRef}
        src={src}
        poster={poster ?? undefined}
        title={title}
        playsInline
        preload="metadata"
        controls={false}
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        className="absolute inset-0 h-full w-full bg-black object-contain"
        onError={(event) => {
          const mediaError = event.currentTarget.error
          if (!mediaError || mediaError === previousErrorRef.current) {
            return
          }

          previousErrorRef.current = mediaError
          clearPlayRecoveryTimeout()
          setIsWaiting(false)
          setHasPlaybackError(true)
          setPaused(true)
          onPlaybackError?.()
        }}
        onLoadStart={() => {
          setHasPlaybackError(false)
          setIsWaiting(true)
        }}
        onLoadedMetadata={(event) => {
          const media = event.currentTarget
          const durationValue = Number.isFinite(media.duration) ? media.duration : 0
          if (
            !hasAppliedInitialSeekRef.current &&
            typeof initialProgressSeconds === 'number' &&
            Number.isFinite(initialProgressSeconds) &&
            initialProgressSeconds > 0 &&
            durationValue > 0
          ) {
            media.currentTime = Math.min(initialProgressSeconds, Math.max(durationValue - 0.25, 0))
            hasAppliedInitialSeekRef.current = true
          }
          setDuration(durationValue)
          setCurrentTime(Number.isFinite(media.currentTime) ? media.currentTime : 0)
          setVolume(media.volume)
          setMuted(media.muted)
          setPlaybackRate(media.playbackRate)
          setHasPlaybackError(false)
          setIsWaiting(false)
        }}
        onLoadedData={() => {
          setHasPlaybackError(false)
          setIsWaiting(false)
        }}
        onDurationChange={(event) => {
          const value = Number.isFinite(event.currentTarget.duration)
            ? event.currentTarget.duration
            : 0
          setDuration(value)
        }}
        onTimeUpdate={(event) => {
          const media = event.currentTarget
          const nextCurrentTime = media.currentTime
          setCurrentTime(nextCurrentTime)

          const checkpointSecond = Math.floor(nextCurrentTime)
          if (checkpointSecond < 0 || checkpointSecond === lastCheckpointSecondRef.current) {
            return
          }

          const shouldCheckpoint =
            lastCheckpointSecondRef.current < 0 ||
            checkpointSecond - lastCheckpointSecondRef.current >= 10
          if (!shouldCheckpoint) {
            return
          }

          lastCheckpointSecondRef.current = checkpointSecond
          emitPlaybackCheckpoint(nextCurrentTime, media.duration, false)
        }}
        onPlay={() => setPaused(false)}
        onPause={() => {
          const media = videoRef.current
          if (media && Number.isFinite(media.currentTime)) {
            emitPlaybackCheckpoint(media.currentTime, media.duration, false)
          }
          clearPlayRecoveryTimeout()
          setPaused(true)
        }}
        onEnded={(event) => {
          const media = event.currentTarget
          emitPlaybackCheckpoint(
            Number.isFinite(media.duration) ? media.duration : media.currentTime,
            media.duration,
            true
          )
        }}
        onWaiting={() => setIsWaiting(true)}
        onStalled={() => setIsWaiting(true)}
        onPlaying={() => {
          clearPlayRecoveryTimeout()
          setIsWaiting(false)
        }}
        onCanPlay={() => setIsWaiting(false)}
        onVolumeChange={(event) => {
          setVolume(event.currentTarget.volume)
          setMuted(event.currentTarget.muted)
        }}
        onRateChange={(event) => {
          setPlaybackRate(event.currentTarget.playbackRate)
        }}
        onClick={togglePaused}
      >
        <track kind="captions" src={EMPTY_CAPTIONS_VTT} srcLang="ru" label="Captions" default />
      </video>

      {isWaiting && !hasPlaybackError && !paused ? (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <div className="h-16 w-16 animate-spin rounded-full border-4 border-white/30 border-t-white" />
        </div>
      ) : null}
      {hasPlaybackError ? (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/40 px-4">
          <p className="text-sm text-white/90">
            Не удалось загрузить видео. Попробуйте открыть другой материал.
          </p>
        </div>
      ) : null}

      <div className="absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/90 via-black/50 to-transparent px-4 pb-3 pt-7 sm:px-5">
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={duration > 0 ? effectiveSeekTime : 0}
          aria-label="Video progress"
          disabled={duration <= 0}
          className="h-[3px] w-full cursor-pointer appearance-none rounded-full bg-white/20 accent-white"
          style={{
            background: `linear-gradient(90deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.9) ${seekPercent}%, rgba(255,255,255,0.25) ${seekPercent}%, rgba(255,255,255,0.25) 100%)`,
          }}
          onChange={(event) => {
            setSeekDraft(Number(event.currentTarget.value))
          }}
          onPointerUp={(event) =>
            commitSeek(Number((event.currentTarget as HTMLInputElement).value))
          }
          onKeyUp={(event) => {
            if (
              event.key === 'ArrowLeft' ||
              event.key === 'ArrowRight' ||
              event.key === 'Home' ||
              event.key === 'End'
            ) {
              commitSeek(Number((event.currentTarget as HTMLInputElement).value))
            }
          }}
        />

        <div className="mt-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label={paused ? 'Play video' : 'Pause video'}
              className="inline-flex h-8 w-8 items-center justify-center text-white transition-opacity hover:opacity-80"
              onClick={togglePaused}
            >
              {paused ? <Play size={18} /> : <Pause size={18} />}
            </button>
            <span className="text-xs text-white">{controlsTime}</span>
          </div>

          <div className="relative flex items-center gap-3">
            <button
              type="button"
              aria-label="Playback speed"
              className="text-xs text-white transition-opacity hover:opacity-80"
              onClick={() => setIsSettingsOpen((open) => !open)}
            >
              {formatRate(playbackRate)}
            </button>

            <button
              type="button"
              aria-label={muted ? 'Unmute' : 'Mute'}
              className="inline-flex h-8 w-8 items-center justify-center text-white transition-opacity hover:opacity-80"
              onClick={toggleMute}
            >
              {muted || volume <= 0.01 ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>

            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={volumeValue}
              aria-label="Volume"
              className="h-[3px] w-20 cursor-pointer appearance-none rounded-full bg-white/20 accent-white"
              onChange={(event) => changeVolume(Number(event.currentTarget.value))}
            />

            <button
              type="button"
              aria-label="Player settings"
              className={cn(
                'inline-flex h-8 w-8 items-center justify-center transition-opacity hover:opacity-80',
                isSettingsOpen ? 'text-white' : 'text-white/85'
              )}
              onClick={() => setIsSettingsOpen((open) => !open)}
            >
              <Settings size={18} />
            </button>

            <button
              type="button"
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              className="inline-flex h-8 w-8 items-center justify-center text-white transition-opacity hover:opacity-80"
              onClick={() => {
                void toggleFullscreen()
              }}
            >
              <Expand size={18} />
            </button>

            {isSettingsOpen ? (
              <div className="absolute bottom-12 right-0 w-56 rounded-lg border border-white/15 bg-[#1A1A1A] p-3 shadow-[0_16px_36px_rgba(0,0,0,0.55)]">
                <div className="space-y-2">
                  <p className="text-[11px] uppercase tracking-wide text-white/60">Speed</p>
                  <div className="grid grid-cols-3 gap-1">
                    {PLAYBACK_RATES.map((rate) => (
                      <button
                        key={rate}
                        type="button"
                        className={cn(
                          'rounded px-2 py-1 text-xs transition-colors',
                          playbackRate === rate
                            ? 'bg-white text-black'
                            : 'bg-white/10 text-white hover:bg-white/20'
                        )}
                        onClick={() => applyPlaybackRate(rate)}
                      >
                        {formatRate(rate)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

CatalogVideoPlayer.displayName = 'CatalogVideoPlayer'
